import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { companyInfo, services } from '../data/mock';
import { toast } from 'sonner';

const Contact = () => {

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleServiceChange = (value) => {
    setFormData({
      ...formData,
      service: value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    setIsSubmitting(true);

    // ===== YOUR WHATSAPP NUMBER =====
    const phoneNumber = "919560039943";

    const message =
`Hello S2U Enterprises,

New Quote Request:

Name: ${formData.name}
Email: ${formData.email}
Phone: ${formData.phone}
Service: ${formData.service}

Project Details:
${formData.message}
`;

    const whatsappURL =
      `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;

    window.open(whatsappURL, "_blank");

    toast.success("Redirecting to WhatsApp with your details");

    setFormData({
      name: '',
      email: '',
      phone: '',
      service: '',
      message: ''
    });

    setIsSubmitting(false);
  };


  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[400px] flex items-center bg-gradient-to-r from-blue-600 to-slate-700">
        <div className="container mx-auto px-4 z-10">
          <Badge className="mb-4 bg-white text-blue-600 border-0">Get in Touch</Badge>
          <h1 className="text-5xl font-bold text-white mb-6">
            Let's Build Something Great Together
          </h1>
          <p className="text-xl text-blue-100 max-w-3xl">
            Ready to start your project? Get in touch with our team for a free consultation and quote.
          </p>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 -mt-20 relative z-10">
            <Card className="border-0 shadow-xl hover:shadow-2xl transition-all">
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 rounded-full bg-blue-600 text-white flex items-center justify-center mx-auto mb-4">
                  <Phone size={24} />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">Call Us</h3>
                <a href={`tel:${companyInfo.phone}`} className="text-blue-600 hover:text-blue-700 font-medium">
                  {companyInfo.phone}
                </a>
                <p className="text-xs text-slate-500 mt-2">24x7 Available</p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-xl hover:shadow-2xl transition-all">
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 rounded-full bg-blue-600 text-white flex items-center justify-center mx-auto mb-4">
                  <Mail size={24} />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">Email Us</h3>
                <a href={`mailto:${companyInfo.email}`} className="text-blue-600 hover:text-blue-700 font-medium text-sm break-all">
                  {companyInfo.email}
                </a>
                <p className="text-xs text-slate-500 mt-2">Quick Response</p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-xl hover:shadow-2xl transition-all">
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 rounded-full bg-blue-600 text-white flex items-center justify-center mx-auto mb-4">
                  <Clock size={24} />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">Response Time</h3>
                <p className="text-blue-600 font-bold">{companyInfo.responseTime}</p>
                <p className="text-xs text-slate-500 mt-2">Delhi & NCR</p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-xl hover:shadow-2xl transition-all">
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 rounded-full bg-blue-600 text-white flex items-center justify-center mx-auto mb-4">
                  <MapPin size={24} />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">Visit Us</h3>
                <p className="text-slate-600 text-sm">Uttam Nagar</p>
                <p className="text-xs text-slate-500 mt-2">New Delhi</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Contact Form & Map Section */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              <Badge className="mb-4 bg-blue-50 text-blue-600 border-blue-200">Request a Quote</Badge>
              <h2 className="text-3xl font-bold text-slate-900 mb-4">Get Free Consultation</h2>
              <p className="text-slate-600 mb-8">
                Fill out the form below and our team will get back to you within 24 hours.
              </p>
              
              <Card className="border-slate-200 shadow-lg">
                <CardContent className="p-8">
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <Label htmlFor="name" className="text-slate-700 font-medium">Full Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Enter your full name"
                        className="mt-2 border-slate-300 focus:border-blue-600"
                      />
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="email" className="text-slate-700 font-medium">Email Address *</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          required
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="your@email.com"
                          className="mt-2 border-slate-300 focus:border-blue-600"
                        />
                      </div>
                      <div>
                        <Label htmlFor="phone" className="text-slate-700 font-medium">Phone Number *</Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          required
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="+91 XXXXX XXXXX"
                          className="mt-2 border-slate-300 focus:border-blue-600"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <Label htmlFor="service" className="text-slate-700 font-medium">Service Required *</Label>
                      <Select value={formData.service} onValueChange={handleServiceChange} required>
                        <SelectTrigger className="mt-2 border-slate-300 focus:border-blue-600">
                          <SelectValue placeholder="Select a service" />
                        </SelectTrigger>
                        <SelectContent>
                          {services.map((service) => (
                            <SelectItem key={service.id} value={service.title}>
                              {service.title}
                            </SelectItem>
                          ))}
                          <SelectItem value="other">Other Services</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label htmlFor="message" className="text-slate-700 font-medium">Project Details *</Label>
                      <Textarea
                        id="message"
                        name="message"
                        required
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Tell us about your project requirements..."
                        rows={5}
                        className="mt-2 border-slate-300 focus:border-blue-600 resize-none"
                      />
                    </div>
                    
                    <Button
                      type="submit"
                      size="lg"
                      disabled={isSubmitting}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white h-12"
                    >
                      {isSubmitting ? (
                        <>Submitting...</>
                      ) : (
                        <>
                          Submit Request <Send className="ml-2" size={18} />
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Office Info & Map */}
            <div className="space-y-8">
              <div>
                <Badge className="mb-4 bg-blue-50 text-blue-600 border-blue-200">Our Office</Badge>
                <h2 className="text-3xl font-bold text-slate-900 mb-4">Visit Our Office</h2>
                <p className="text-slate-600 mb-8">
                  We're located in New Delhi and serve clients across India with our specialized services.
                </p>
                
                <Card className="border-slate-200 shadow-lg mb-6">
                  <CardContent className="p-6 space-y-4">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                        <MapPin className="text-blue-600" size={20} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900 mb-1">Address</h3>
                        <p className="text-slate-600 text-sm leading-relaxed">{companyInfo.address}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                        <Phone className="text-blue-600" size={20} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900 mb-1">Phone</h3>
                        <p className="text-slate-600 text-sm">{companyInfo.phone}</p>
                        <p className="text-slate-600 text-sm">85950 77979</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                        <Mail className="text-blue-600" size={20} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900 mb-1">Email</h3>
                        <p className="text-slate-600 text-sm break-all">{companyInfo.email}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                        <Clock className="text-blue-600" size={20} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900 mb-1">Working Hours</h3>
                        <p className="text-slate-600 text-sm">24x7 Service Available</p>
                        <p className="text-slate-500 text-xs mt-1">Response Time: {companyInfo.responseTime}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Google Map */}
                <Card className="border-slate-200 shadow-lg overflow-hidden">
                  <div className="h-80 bg-slate-200">
                    <iframe
                      title="S2U Enterprises Location"
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3502.0726928515!2d77.0594!3d28.6258!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjjCsDM3JzMyLjkiTiA3N8KwMDMnMzMuOCJF!5e0!3m2!1sen!2sin!4v1234567890"
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      allowFullScreen=""
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                    ></iframe>
                  </div>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Contact Us Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-slate-900 mb-6">Why Choose S2U Enterprises?</h2>
            <div className="grid md:grid-cols-3 gap-8 mt-12">
              <div>
                <div className="w-14 h-14 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="text-blue-600" size={28} />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">Quick Response</h3>
                <p className="text-slate-600 text-sm">Get responses within 3 hours in Delhi & NCR region with 24x7 availability.</p>
              </div>
              <div>
                <div className="w-14 h-14 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="text-blue-600" size={28} />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">Expert Team</h3>
                <p className="text-slate-600 text-sm">Experienced professionals with expertise in medical infrastructure and technical services.</p>
              </div>
              <div>
                <div className="w-14 h-14 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="text-blue-600" size={28} />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">Quality Assurance</h3>
                <p className="text-slate-600 text-sm">Commitment to quality standards at every step of the project lifecycle.</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
